import {
    RESOURCE_NAME as LOCALES_RESOURCE_NAME,
    keys as localeKeys,
} from "@app/shared/utils/locales";
import { Button, type ButtonProps } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { type FC, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { useSupplierTemplateQuestionnaire } from "../../Questions/data";
import {
    useExportLastQuestionnaireFilesOfSuppliers,
    useExportLastQuestionnaireOfSuppliers,
} from "../data";
import { useExportQuestionsDropdownTranslations } from "./i18n";

const ExportAnswersItem: FC<{
    locale: string;
    onExport: (locale: string) => void;
}> = ({ locale, onExport }) => {
    const { t, keys } = useExportQuestionsDropdownTranslations();
    const { t: tLocale } = useTranslation(LOCALES_RESOURCE_NAME);
    const localeName = tLocale(
        localeKeys[locale as keyof typeof localeKeys] ?? locale,
    );

    const handleClick = () => {
        onExport(locale);
    };

    return (
        <Dropdown.Item onClick={handleClick}>
            <Dropdown.ItemText>
                {t(keys.export_answers_in_locale, { locale: localeName })}
            </Dropdown.ItemText>
            <Dropdown.ItemIcon name="arrowDownload" />
        </Dropdown.Item>
    );
};

export const ExportQuestionsDropdown: FC<
    Omit<ButtonProps, "onClick" | "children" | "loading"> & {
        ref?: Ref<HTMLButtonElement>;
    }
> = ({ ...buttonProps }) => {
    const {
        exportLastQuestionnaireOfSuppliers,
        isPending: isExportingAnswers,
    } = useExportLastQuestionnaireOfSuppliers();

    const {
        exportLastQuestionnaireFilesOfSuppliers,
        isPending: isExportingFiles,
    } = useExportLastQuestionnaireFilesOfSuppliers();

    const { templateQuestionnaire } = useSupplierTemplateQuestionnaire();

    const availableLocales =
        templateQuestionnaire?.locale?.availableLocales ?? [];

    const handleExportAnswers = (locale: string) => {
        exportLastQuestionnaireOfSuppliers({ locale });
    };

    const handleDownloadFiles = () => {
        exportLastQuestionnaireFilesOfSuppliers();
    };

    const isExporting = isExportingAnswers || isExportingFiles;

    const { t, keys } = useExportQuestionsDropdownTranslations();

    return (
        <Dropdown>
            <Dropdown.Trigger asChild>
                <Button loading={isExporting} {...buttonProps}>
                    {t(keys.trigger_button)}
                    <Button.Icon name="chevronDown" />
                </Button>
            </Dropdown.Trigger>
            <Dropdown.Content>
                {availableLocales.map((locale) => (
                    <ExportAnswersItem
                        key={locale}
                        locale={locale}
                        onExport={handleExportAnswers}
                    />
                ))}
                <Dropdown.Item onClick={handleDownloadFiles}>
                    <Dropdown.ItemText>
                        {t(keys.export_files)}
                    </Dropdown.ItemText>
                    <Dropdown.ItemIcon name="arrowDownload" />
                </Dropdown.Item>
            </Dropdown.Content>
        </Dropdown>
    );
};
